"use client";

import { useState, useEffect, useRef } from "react";
import testimonials from "../data/testomonial.json";
import { Play } from "lucide-react";
import { track } from "../lib/track";

export default function TestimonialHeroCarousel() {
  const [index, setIndex] = useState(0);
  const [displayedIndex, setDisplayedIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [hovered, setHovered] = useState(false);
  const transitioning = useRef(false);

  type Testimonial = {
    thumbnail?: string;
    name: string;
    quote: string;
    excerpt?: string;
    body?: string;
    id: string;
    type: string;
    title: string;
    src?: string;
    label?: string;
  };

  const testimonial: Testimonial = testimonials[displayedIndex];

  // When index changes, fade out → swap content → fade in
  useEffect(() => {
    if (index === displayedIndex) return;
    if (transitioning.current) return;
    transitioning.current = true;
    setVisible(false);
    const swap = setTimeout(() => {
      setDisplayedIndex(index);
      setVisible(true);
      transitioning.current = false;
    }, 300);
    return () => clearTimeout(swap);
  }, [index, displayedIndex]);

  // Auto-advance
  useEffect(() => {
    if (showModal || hovered) return;
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [showModal, hovered]);

  return (
    <section
      className="relative flex min-h-[50vh] w-full items-center justify-center overflow-hidden py-12"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Background */}
      <img
        src="/images/CFMontage.webp"
        alt=""
        aria-hidden="true"
        loading="lazy"
        width={1200}
        height={675}
        className="absolute inset-0 h-[60vh] w-full object-cover"
      />
      <div className="absolute inset-0" />

      {/* Content */}
      <div className="relative z-10 flex w-full max-w-3xl items-center justify-center px-6">
        <div
          className="relative w-full rounded-2xl bg-white/90 p-6 shadow-xl transition-opacity duration-300"
          style={{ opacity: visible ? 1 : 0 }}
        >
          {/* Logo */}
          <img
            src="/images/CFOW_circle.webp"
            alt="CF Organic Wellness"
            width={40}
            height={40}
            className="absolute right-4 top-4 h-10 w-10 opacity-80"
          />

          {testimonial.type === "video" ? (
            <div className="cursor-pointer">
              <div
                className="relative mx-auto"
                onClick={() => {
                  track(
                    "video_play",
                    {
                      label: testimonial.label || "Default Label",
                      component: "SocialProofCarousel",
                    },
                    {
                      event: "video_play",
                      testimonial_id: testimonial.id,
                      type: testimonial.type as "video" | "text",
                      component: "SocialProofCarousel",
                      label: testimonial.label || "Default Label",
                    }
                  );
                  setShowModal(true);
                }}
              >
                <img
                  src={testimonial.thumbnail}
                  alt={`${testimonial.name} testimonial`}
                  width={480}
                  height={270}
                  className="mx-auto block h-auto w-auto max-h-[38vh] max-w-full rounded-xl shadow-md"
                />
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="rounded-full bg-white/90 p-4 shadow-md">
                    <Play className="h-8 w-8 text-[var(--color-brand)]" />
                  </div>
                </div>
              </div>

              <p className="mt-4 text-center text-lg font-semibold text-[var(--fern-dk)]">
                &ldquo;{testimonial.quote}&rdquo;
              </p>
              {testimonial.excerpt && (
                <p className="mt-2 text-center text-sm text-[var(--fern-dk)]/80">
                  {testimonial.excerpt.trim()}
                </p>
              )}
            </div>
          ) : (
            <div className="mx-auto max-w-2xl text-center">
              <p className="mb-4 text-lg font-semibold text-[var(--fern-dk)]">
                &ldquo;{testimonial.quote}&rdquo;
              </p>
              {testimonial.body && (
                <p className="text-sm text-[var(--fern-dk)]/80">{testimonial.body}</p>
              )}
            </div>
          )}

          <div className="mt-4 text-center text-xs font-medium uppercase tracking-wide text-[var(--fern-dk)]/70">
            — {testimonial.name}, {testimonial.title}
          </div>
        </div>
      </div>

      {/* Dots — p-3 gives a 32px touch target while keeping the 8px visual dot */}
      <div className="absolute bottom-6 z-10 flex">
        {testimonials.map((t, i) => (
          <button
            key={t.id}
            onClick={() => setIndex(i)}
            className="p-3 flex items-center justify-center"
            aria-label={`Go to testimonial ${i + 1}`}
          >
            <span
              className={`h-2 w-2 rounded-full block ${
                i === displayedIndex ? "bg-[var(--color-brand)]" : "bg-white/60"
              }`}
            />
          </button>
        ))}
      </div>

      {/* Video Modal */}
      {testimonial.type === "video" && showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowModal(false)}
        >
          <video
            src={testimonial.src}
            controls
            autoPlay
            className="h-2/3 w-full max-w-2xl rounded-xl shadow-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
